import { FC, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { formatDuration, intervalToDuration } from 'date-fns';
import { DateUtils, Tr } from '@utils';
import { SortOrder, sortingMapperToQuery, sortingMapperToTable } from '@models';
import { useFindAllQuery, useService } from '@hooks';
import { UserInvitation, adminService } from '@services';
import { Box, Stack, Typography } from '@mui/material';
import { DataTable, NoDataAnimation } from '@components/common';

type Entity = UserInvitation;

export interface IAdminDashboardNotAcceptedUsersProps {}

export const AdminDashboardNotAcceptedUsers: FC<IAdminDashboardNotAcceptedUsersProps> = () => {
	const { query, setPagination, setSorting } = useFindAllQuery<Entity>({
		page: 0,
		pageSize: 25,
		sort: [{ by: 'email', order: SortOrder.ASC }]
	});
	const { data: notAcceptedUsers } = useService(() => adminService.getNotAcceptedUsers(query), [query]);

	const columns = useMemo<ColumnDef<Entity>[]>(
		() => [
			{
				accessorKey: 'email',
				enableSorting: true,
				header: () => <Tr.Portal path="admin-dashboard.email" />,
				cell: ({ row }) => row.original.email
			},
			{
				accessorKey: 'name',
				enableSorting: true,
				header: () => <Tr.Portal path="admin-dashboard.first-name" />,
				cell: ({ row }) => row.original.name
			},
			{
				accessorKey: 'surname',
				enableSorting: true,
				header: () => <Tr.Portal path="admin-dashboard.last-name" />,
				cell: ({ row }) => row.original.surname
			},
			{
				accessorKey: 'organization',
				enableSorting: true,
				header: () => <Tr.Portal path="admin-dashboard.organization" />,
				cell: ({ row }) => row.original.organization?.name || row.original.organization?.id || ''
			},
			{
				accessorKey: 'roleId',
				enableSorting: true,
				header: () => <Tr.Portal path="admin-dashboard.role" />,
				cell: ({ row }) => row.original.role?.name || row.original.roleId || ''
			},
			{
				accessorKey: 'createdAt',
				enableSorting: true,
				header: () => <Tr.Portal path="admin-dashboard.invited-on" />,
				cell: ({ row }) => (
					<Stack direction="column" spacing={1}>
						<Typography variant="body2">{DateUtils.format(row.original.createdAt)}</Typography>
						<Typography variant="caption" sx={{ color: 'grey.700' }}>
							{formatDuration(intervalToDuration({ start: row.original.createdAt, end: new Date() }), {
								format: ['years', 'months', 'days', 'hours', 'minutes']
							})}
						</Typography>
					</Stack>
				)
			}
		],
		[]
	);

	return Array.isArray(notAcceptedUsers?.data) && notAcceptedUsers?.data.length ? (
		<Box sx={{ flexGrow: 1, overflow: 'auto' }}>
			<DataTable
				columns={columns}
				data={notAcceptedUsers.data}
				comparator={(d1, d2) => d1.id === d2.id}
				paginationComponentProps={{ rowsPerPageOptions: [10, 25, 50] }}
				paginationType="ssr"
				pageIndex={notAcceptedUsers?.page ?? query.page}
				pageSize={notAcceptedUsers?.pageSize ?? query.pageSize}
				totalCount={notAcceptedUsers?.total || 0}
				onPageChange={setPagination}
				sortingType="ssr"
				sorting={sortingMapperToTable(query.sort)}
				onSortingChange={(newSorting) => setSorting(sortingMapperToQuery(newSorting))}
				headerTypographyProps={{ variant: 'caption' }}
				emptyComponent={<NoDataAnimation message={<Tr.Portal path="admin-dashboard.no-user-message" />} />}
			/>
		</Box>
	) : null;
};
